import {
  IconClock,
  IconDownload,
  IconPigMoney,
  IconPuzzle,
  IconRocket,
} from "@tabler/icons-react";
import { Quote, ArrowRight } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
// Note: AOS is initialized in Features component which loads after Hero
import { Link } from "react-scroll";
import { HoverBorderGradient } from "../animations/hover-border-gradient";
import { linkFree } from "../../constants/tier";
import { Container } from "../ui/container";
import { Heading } from "../ui/heading";

export const Hero = () => {
  return (
    <div
      className="flex flex-col pt-32 pb-20 relative overflow-visible"
    >
      {/* Bottom gradient fade to black */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent via-slate-900/50 to-black pointer-events-none z-10" />
      {/* Mobile Layout - Stacked */}
      <Container className="flex flex-col items-center justify-center dark md:hidden">
        <Heading
          as="h1"
          className="text-5xl font-semibold max-w-6xl mx-auto text-center relative z-10 py-6"
        >
          The All-in-One{" "}
          <span className="bg-clip-text text-transparent bg-no-repeat bg-gradient-to-r from-fuchsia-500 via-fuchsia-600 to-pink-500">
            Control Center
          </span>{" "}
          for Your WooCommerce Store.
        </Heading>
        <div className="!text-slate-400 max-w-xl text-center text-lg">
          Forget slow CSV imports and manual updates. Bulk edit thousands of
          products and launch complex discounts in seconds. Import and sync
          everything with and Google Sheets instantly.
        </div>

        {/* Value Badges - Mobile */}
        <div className="flex flex-wrap items-center justify-center gap-2 mt-6 mb-2 px-4">
          {[
            {
              icon: IconClock,
              text: "90% Less Admin Time",
              color: "from-emerald-400 to-green-500",
            },
            {
              icon: IconPuzzle,
              text: "5-in-1 Plugins Replaced",
              color: "from-blue-400 to-cyan-500",
            },
            {
              icon: IconPigMoney,
              text: "$200+ Saved Annually",
              color: "from-amber-400 to-orange-500",
            },
            {
              icon: IconRocket,
              text: "10x Faster Workflow",
              color: "from-fuchsia-400 to-pink-500",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 backdrop-blur-sm border border-white/10"
            >
              <div
                className={`p-1 rounded-full bg-gradient-to-r ${item.color}`}
              >
                <item.icon className="h-3 w-3 text-white" stroke={2.5} />
              </div>
              <span
                className={`text-xs font-medium bg-gradient-to-r ${item.color} bg-clip-text text-transparent`}
              >
                {item.text}
              </span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 justify-center my-8 relative z-10 dark">
          <HoverBorderGradient
            containerClassName="rounded-full"
            as="button"
            onClick={linkFree}
            className="dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2 group"
          >
            <IconDownload className="h-4 w-4" stroke={2} />
            <span>Download Free</span>
          </HoverBorderGradient>
          <Link
            activeClass="active"
            spy={true}
            to="features"
            smooth="easeInOutQuint"
            duration={1000}
            delay={200}
            offset={20}
          >
            <button className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors group">
              <span>Explore Features</span>
              <ArrowRight className="group-hover:translate-x-1 h-3 w-3 transition-transform duration-200" />
            </button>
          </Link>
        </div>

        {/* Video - Mobile - Commented out for performance testing
        <div className="flex items-center justify-center relative p-0 cursor-pointer w-full">
          <div className="w-full relative">
            <Card></Card>
          </div>
        </div>
        */}
      </Container>

      {/* Desktop Layout - Centered */}
      <Container className="hidden md:flex flex-col items-center justify-center dark">
        <Heading
          as="h1"
          className="text-5xl lg:text-6xl xl:text-7xl font-semibold max-w-5xl mx-auto text-center relative z-10 py-6"
          data-aos="fade-up"
        >
          The All-in-One{" "}
          <span className="bg-clip-text text-transparent bg-no-repeat bg-gradient-to-r from-fuchsia-500 via-fuchsia-600 to-pink-500">
            Control Center
          </span>{" "}
          for Your WooCommerce Store.
        </Heading>
        <div
          className="!text-slate-400 max-w-3xl text-center text-lg lg:text-xl"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          Forget slow CSV imports and manual updates. Bulk edit thousands
          of products and launch complex discounts in seconds. Import and sync
          everything with and Google Sheets instantly.
        </div>

        {/* Value Badges - Desktop */}
        <div
          className="flex flex-wrap items-center justify-center gap-2 mt-6 mb-2"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          {[
            {
              icon: IconClock,
              text: "90% Less Time",
              color: "from-emerald-400 to-green-500",
            },
            {
              icon: IconPuzzle,
              text: "5-in-1 Solution",
              color: "from-blue-400 to-cyan-500",
            },
            {
              icon: IconPigMoney,
              text: "$200+ Saved",
              color: "from-amber-400 to-orange-500",
            },
            {
              icon: IconRocket,
              text: "10x Faster",
              color: "from-fuchsia-400 to-pink-500",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="group flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-300"
            >
              <div
                className={`p-1 rounded-full bg-gradient-to-r ${item.color}`}
              >
                <item.icon className="h-3 w-3 text-white" stroke={2.5} />
              </div>
              <span
                className={`text-xs font-medium bg-gradient-to-r ${item.color} bg-clip-text text-transparent whitespace-nowrap`}
              >
                {item.text}
              </span>
            </div>
          ))}
        </div>

        <div
          className="flex items-center gap-3 mt-6 relative z-10 dark"
          data-aos="fade-up"
        >
          <HoverBorderGradient
            containerClassName="rounded-full"
            as="button"
            onClick={linkFree}
            className="dark:bg-black bg-white text-black dark:text-white flex items-center space-x-2 group"
          >
            <IconDownload className="h-4 w-4" stroke={2} />
            <span>Download Free</span>
          </HoverBorderGradient>
          <Link
            activeClass="active"
            spy={true}
            to="features"
            smooth="easeInOutQuint"
            duration={1000}
            delay={200}
            offset={20}
          >
            <button className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:text-white transition-colors group">
              <span>Explore Features</span>
              <ArrowRight className="group-hover:translate-x-1 h-3 w-3 transition-transform duration-200" />
            </button>
          </Link>
        </div>

        {/* Video - Commented out for performance testing
        <div
          className="w-full max-w-4xl flex items-center justify-center relative cursor-pointer mt-10"
          data-aos="fade-up"
        >
          <Card></Card>
        </div>
        */}
      </Container>

      {/* Expert Testimonials */}
      <div className="max-w-4xl mx-auto px-4 mt-[100px] mb-[-160px] relative z-50 space-y-6">
        {/* Primary Testimonial - Haim Benisty */}
        <div className="relative" data-aos="fade-up">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-fuchsia-600 to-pink-600 rounded-2xl blur opacity-20"></div>
          <div className="absolute -inset-px bg-gradient-to-r from-fuchsia-600 to-pink-600 rounded-2xl opacity-50"></div>

          <div className="relative bg-neutral-900/90 backdrop-blur-sm rounded-2xl p-6 md:p-10">
            <div className="absolute -top-4 left-6 md:left-10">
              <div className="p-3 rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-600">
                <Quote className="h-5 w-5 text-white" fill="white" />
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
              <div className="flex items-center gap-3 md:gap-4 shrink-0">
                <div className="transform hover:scale-105 transition-transform duration-300">
                  <img
                    src="https://whizmanage.com/wp-content/uploads/2025/07/Elements.png"
                    alt="Schooliner Logo"
                    width={64}
                    height={64}
                    loading="lazy"
                    decoding="async"
                    className="h-12 w-12 md:h-16 md:w-16 object-contain drop-shadow-xl"
                  />
                </div>
                <div className="relative">
                  <div className="absolute -inset-1 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full blur opacity-40"></div>
                  <img
                    src="https://whizmanage.com/wp-content/uploads/2025/07/haim2.jpg"
                    alt="Haim Benisty"
                    width={72}
                    height={72}
                    loading="lazy"
                    decoding="async"
                    className="relative h-14 w-14 md:h-18 md:w-18 rounded-full object-cover drop-shadow-xl transform hover:scale-105 transition-transform duration-300"
                    style={{ objectPosition: "50% 20%" }}
                  />
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <blockquote className="text-lg md:text-xl text-neutral-200 leading-relaxed mb-6">
                  "Managing a WooCommerce store has never been easier. WhizManage transforms hours of tedious work into minutes, giving store owners the freedom to focus on what really matters - growing their business."
                </blockquote>

                <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                  <div>
                    <div className="font-bold text-white text-lg">Haim Benisty</div>
                    <div className="text-sm text-neutral-400">WooCommerce Expert & Lead Mentor at Schooliner</div>
                  </div>
                  <div className="hidden md:block h-8 w-px bg-neutral-700"></div>
                  <div className="flex items-center justify-center md:justify-start gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="h-4 w-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Testimonial - Tom Aviram */}
        <div className="relative" data-aos="fade-up" data-aos-delay="100">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl blur opacity-15"></div>
          <div className="absolute -inset-px bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl opacity-30"></div>

          <div className="relative bg-neutral-900/80 backdrop-blur-sm rounded-xl p-4 md:p-6">
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <div className="relative shrink-0">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full blur opacity-30"></div>
                <img
                  src="https://whizmanage.com/wp-content/uploads/2026/01/tom.png"
                  alt="Tom Aviram"
                  width={56}
                  height={56}
                  loading="lazy"
                  decoding="async"
                  className="relative h-12 w-12 md:h-14 md:w-14 rounded-full object-cover drop-shadow-lg"
                />
              </div>

              <div className="flex-1 text-center md:text-left">
                <blockquote className="text-base md:text-lg text-neutral-300 leading-relaxed mb-3">
                  "WhizManage is the missing link for WooCommerce store owners who want to scale without the stress. It transforms the backend experience from a chore into a competitive advantage."
                </blockquote>

                <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3">
                  <div>
                    <span className="font-semibold text-white">Tom Aviram</span>
                    <span className="text-neutral-500 mx-2 hidden md:inline">•</span>
                    <span className="text-sm text-neutral-400 block md:inline">Web Designer & UX Specialist</span>
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-0.5 mt-1 md:mt-0">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="h-3 w-3 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Video poster image for loading state - use WebP for better compression
const VIDEO_POSTER = "https://whizmanage.com/wp-content/uploads/2026/01/video-poster.png";
// Fallback for browsers that don't support WebP
const VIDEO_POSTER_FALLBACK = "https://whizmanage.com/wp-content/uploads/2026/01/video-poster.png";
// Video aspect ratio (width:height) - 707x329
const VIDEO_ASPECT_RATIO = 707 / 329; // ~2.15
// Video URLs - prefer WebM for better compression
const VIDEO_WEBM = "https://whizmanage.com/wp-content/uploads/2024/10/Whizmanage-App-Demo-Overview.mp4.mp4";
const VIDEO_MP4 = "https://whizmanage.com/wp-content/uploads/2024/10/Whizmanage-App-Demo-Overview.mp4.mp4";

export const Card = ({ children }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Use Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "100px", // Start loading 100px before visible
        threshold: 0.1,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Handle video load
  const handleCanPlay = useCallback(() => {
    setIsLoaded(true);
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay was prevented, user interaction required
      });
    }
  }, []);

  // Calculate padding based on aspect ratio (height = 100% / aspectRatio)
  const aspectPadding = `${(1 / VIDEO_ASPECT_RATIO) * 100}%`;

  return (
    <div
      ref={containerRef}
      style={{
        boxShadow:
          "0 0 #0000004d, 0 9px 20px #0000004a, 0 37px 37px #00000042, 0 84px 50px #00000026, 0 149px 60px #0000000a, 0 233px 65px #00000003",
      }}
      className="w-full z-40 group mx-auto isolate relative overflow-hidden rounded-2xl bg-neutral-900"
    >
      {/* Aspect ratio container to prevent layout shift */}
      <div className="relative w-full" style={{ paddingBottom: aspectPadding }}>
        {/* Poster/Placeholder while loading */}
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-neutral-900 rounded-2xl overflow-hidden">
            {/* Background poster image with WebP support */}
            <picture>
              <source srcSet={VIDEO_POSTER} type="image/webp" />
              <img
                src={VIDEO_POSTER_FALLBACK}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-50"
                loading="lazy"
                decoding="async"
              />
            </picture>
            {/* Rotating logo overlay */}
            <div className="relative flex flex-col items-center gap-3 z-10">
              <motion.div
                className="h-16 w-16 flex justify-center items-center"
                animate={{
                  scale: [1, 1.5, 1.5, 1, 1],
                  rotate: [0, 0, 180, 180, 0],
                }}
                transition={{
                  duration: 2,
                  ease: "easeInOut",
                  times: [0, 0.2, 0.5, 0.8, 1],
                  repeat: Infinity,
                  repeatDelay: 1,
                }}
              >
                <img
                  src={window.siteUrl + "/wp-content/plugins/innovative-design/public/logo/symbol.svg"}
                  alt="Loading..."
                  className="h-full w-full"
                />
              </motion.div>
              <span className="text-slate-400 text-sm">Loading video...</span>
            </div>
          </div>
        )}

        {isVisible && (
          <video
            ref={videoRef}
            className={`absolute inset-0 rounded-2xl w-full h-full object-cover transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            poster={VIDEO_POSTER}
            loop
            muted
            playsInline
            preload="metadata"
            onCanPlay={handleCanPlay}
            aria-label="WhizManage App Demo Overview - product demonstration video"
          >
            {/* WebM first for browsers that support it (smaller file size) */}
            <source src={VIDEO_WEBM} type="video/webm" />
            {/* MP4 fallback */}
            <source src={VIDEO_MP4} type="video/mp4" />
            <track
              kind="captions"
              src="https://whizmanage.com/wp-content/uploads/2024/10/whizmanage-demo-captions.vtt"
              srcLang="en"
              label="English"
              default
            />
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      {children}
    </div>
  );
};
